import { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthPage } from './components/auth/AuthPage';
import { LandingPage } from './components/LandingPage';
import { Navigation } from './components/Navigation';
import { AdminDashboard } from './components/AdminDashboard';
import { StudentDashboard } from './components/StudentDashboard';
import { FacultyDashboard } from './components/FacultyDashboard';
import { Clock, ShieldCheck } from 'lucide-react';

function AppContent() {
  const { user, loading, signOut } = useAuth();
  const [showLanding, setShowLanding] = useState(true);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 rounded-full mx-auto mb-4"></div>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4 absolute top-0 left-1/2 -translate-x-1/2"></div>
          </div>
          <p className="text-muted-foreground font-medium mt-2">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    if (showLanding) {
      return <LandingPage onGetStarted={() => setShowLanding(false)} />;
    }
    return <AuthPage onBackToLanding={() => setShowLanding(true)} />;
  }

  // FINAL GUARD: Block unverified non-admin users even if they somehow bypass all other checks
  if (user.isVerified === false && user.role !== 'administrator') {
    const handleForceLogout = async () => {
      try {
        await signOut();
      } catch (error) {
        console.error('Logout error:', error);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
              <Clock className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Account Pending Verification</h2>
              <p className="text-gray-600 mt-3 text-sm">
                Hello <strong>{user.name}</strong>, your account has been registered but is still waiting for administrator approval.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800 text-sm">What happens next?</span>
              </div>
              <ol className="text-xs text-blue-700 space-y-1 pl-4 list-decimal">
                <li>An administrator will review your registration</li>
                <li>Once approved, you will receive an email notification at <strong>{user.email}</strong></li>
                <li>After that, you can log in and access the system</li>
              </ol>
            </div>
            <button
              onClick={handleForceLogout}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderDashboard = () => {
    switch (user.role) {
      case 'administrator':
        return <AdminDashboard user={user} />;
      case 'student':
        return <StudentDashboard user={user} />;
      case 'faculty':
        return <FacultyDashboard user={user} />;
      default:
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">Invalid user role</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30">
      <Navigation 
        user={user} 
        onLogout={handleLogout}
        onUpdateProfile={() => {
          // Profile updates are handled through auth context
          // This callback is kept for compatibility with Navigation component
        }}
      />
      <main id="main-content" role="main" className={user.role === 'administrator' ? 'animate-in fade-in duration-300' : 'p-4 md:p-6 animate-in fade-in duration-300'}>
        <div className={user.role === 'administrator' ? 'w-full' : 'max-w-7xl mx-auto'}>
          {renderDashboard()}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
